import { useState, useEffect } from "react";
import axios from "axios";
import { API, useAuth } from "@/App";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { format } from "date-fns";
import {
  Heart, MapPin, Compass, Star, Target, Image as ImageIcon,
  MessageCircle, Users, Calendar, Sparkles, Check, Move, Maximize2,
  ChevronLeft, ChevronRight, X
} from "lucide-react";
import { ImagePositionEditor, getImageTransform } from "./ImagePositionEditor";
import AuthedImage, { getPhotoBlobUrl } from "./AuthedImage";

const CATEGORIES = {
  travel: { icon: MapPin, color: "#E07A5F" },
  adventures: { icon: Compass, color: "#81B29A" },
  experiences: { icon: Star, color: "#F2CC8F" },
  goals: { icon: Target, color: "#264653" },
  romance: { icon: Heart, color: "#EF476F" },
};

export const MemoriesTimeline = ({ isOpen, onClose }) => {
  const { token, user } = useAuth();
  const [timeline, setTimeline] = useState([]);
  const [loading, setLoading] = useState(true);
  // Photo loading is now viewport-driven via AuthedImage's module-level
  // cache. No longer holding {memoryId → blobUrl} in component state.
  const [selectingCover, setSelectingCover] = useState(null);
  const [allPhotosForMemory, setAllPhotosForMemory] = useState([]);

  // Slideshow / fullscreen viewer (parity with Scrapbook).
  const [showSlideshow, setShowSlideshow] = useState(false);
  const [slideshowPhotos, setSlideshowPhotos] = useState([]);
  const [slideshowIndex, setSlideshowIndex] = useState(0);

  // Image positioning state
  const [editingImage, setEditingImage] = useState(null); // {memoryId, imageUrl}
  const [imagePositions, setImagePositions] = useState({}); // {memoryId: {x, y, scale}}

  // Load saved positions from backend (with localStorage fallback)
  useEffect(() => {
    const loadPositions = async () => {
      try {
        const res = await axios.get(`${API}/image-positions`);
        if (res.data?.positions && Object.keys(res.data.positions).length > 0) {
          setImagePositions(res.data.positions);
          localStorage.setItem('timeline-image-positions', JSON.stringify(res.data.positions));
          return;
        }
      } catch (e) {}
      const saved = localStorage.getItem('timeline-image-positions');
      if (saved) {
        try { setImagePositions(JSON.parse(saved)); } catch (e) {}
      }
    };
    if (isOpen) loadPositions();
  }, [isOpen]);

  // Open the fullscreen slideshow for a memory. Parity with Scrapbook —
  // uses the shared photoCache so already-loaded thumbs aren't refetched.
  const openSlideshow = async (memory) => {
    const ids = (memory.photo_ids && memory.photo_ids.length > 0)
      ? memory.photo_ids
      : (memory.cover_photo_id ? [memory.cover_photo_id] : []);
    if (ids.length === 0) return;
    const photos = (
      await Promise.all(
        ids.map(async (pid) => {
          try {
            const url = await getPhotoBlobUrl(pid, "download");
            return { id: pid, url };
          } catch (e) {
            return null;
          }
        })
      )
    ).filter(Boolean);
    if (!photos.length) return;
    setSlideshowPhotos(photos);
    setSlideshowIndex(0);
    setShowSlideshow(true);
  };

  const nextSlide = () =>
    setSlideshowIndex((i) => (i + 1) % slideshowPhotos.length);
  const prevSlide = () =>
    setSlideshowIndex((i) => (i - 1 + slideshowPhotos.length) % slideshowPhotos.length);

  const fetchTimeline = async () => {
    try {
      const res = await axios.get(`${API}/memories/timeline`);
      setTimeline(res.data.timeline);
      // Photo loading is now viewport-driven via AuthedImage — no
      // upfront Promise.all that blocked the event loop with N parallel
      // 800KB blob fetches.
    } catch (error) {
      console.error("Failed to fetch timeline");
    } finally {
      setLoading(false);
    }
  };

  // Cover-photo selector still needs full URLs in advance (small grid,
  // user explicitly opens it). Use the shared cache so already-loaded
  // photos are reused.
  const fetchPhotosForCover = async (memory) => {
    if (!memory.photo_ids || memory.photo_ids.length === 0) return;
    const photos = (
      await Promise.all(
        memory.photo_ids.map(async (photoId) => {
          try {
            const url = await getPhotoBlobUrl(photoId, "feed");
            return { id: photoId, url };
          } catch (e) {
            return null;
          }
        })
      )
    ).filter(Boolean);
    setAllPhotosForMemory(photos);
    setSelectingCover(memory);
  };

  const setCoverPhoto = async (memoryId, photoId) => {
    try {
      await axios.post(`${API}/items/${memoryId}/cover-photo/${photoId}`);
      toast.success("Cover photo updated!");
      setSelectingCover(null);
      setAllPhotosForMemory([]);
      fetchTimeline();
    } catch (error) {
      toast.error("Failed to set cover photo");
    }
  };

  const handleSavePosition = (memoryId, position) => {
    const newPositions = { ...imagePositions, [memoryId]: position };
    setImagePositions(newPositions);
    localStorage.setItem('timeline-image-positions', JSON.stringify(newPositions));
    axios.post(`${API}/image-position`, { item_id: memoryId, position }).catch(() => {});
    toast.success("Photo position saved!");
  };

  useEffect(() => {
    if (isOpen) {
      fetchTimeline();
    }
    // No URL.revokeObjectURL — module-level photoCache (AuthedImage)
    // owns the blob URLs and reuses them across dialog open/close cycles.
  }, [isOpen]);

  const groupedTimeline = timeline.reduce((acc, memory) => {
    const dateStr = memory.display_date || memory.completed_at;
    const date = dateStr ? new Date(dateStr) : new Date();
    const key = format(date, "MMMM yyyy");
    if (!acc[key]) acc[key] = [];
    acc[key].push(memory);
    return acc;
  }, {});

  return (
    <>
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl bg-card border-none rounded-2xl p-0 max-h-[90vh] overflow-hidden">
        <DialogHeader className="p-6 pb-4 border-b border-charcoal/5">
          <DialogTitle className="font-serif text-xl text-charcoal flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-primary" />
            Memories Timeline
          </DialogTitle>
          <p className="text-sm text-muted-foreground">
            Your completed adventures in chronological order
          </p>
        </DialogHeader>

        <ScrollArea className="max-h-[75vh]">
          <div className="p-6">
            {loading ? (
              <div className="text-center py-12 text-muted-foreground">Loading memories...</div>
            ) : timeline.length === 0 ? (
              <div className="text-center py-12">
                <div className="w-20 h-20 rounded-full bg-accent/20 flex items-center justify-center mx-auto mb-4">
                  <Sparkles className="w-10 h-10 text-primary" />
                </div>
                <h3 className="font-serif text-xl font-medium text-charcoal mb-2">No memories yet</h3>
                <p className="text-muted-foreground">
                  Complete bucket list items to build your memories timeline
                </p>
              </div>
            ) : (
              <div className="space-y-8">
                {Object.entries(groupedTimeline).map(([month, memories]) => (
                  <div key={month}>
                    <h3 className="font-serif text-lg font-medium text-charcoal mb-4 sticky top-0 bg-card py-2">
                      {month}
                    </h3>
                    
                    <div className="space-y-4 relative">
                      <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-charcoal/10" />
                      
                      {memories.map((memory) => {
                        const category = CATEGORIES[memory.category] || CATEGORIES.experiences;
                        const CategoryIcon = category.icon;
                        const position = imagePositions[memory.id] || { x: 0, y: 0, scale: 1 };
                        
                        return (
                          <div
                            key={memory.id}
                            className="relative pl-12"
                            data-testid={`memory-${memory.id}`}
                          >
                            <div
                              className="absolute left-2 w-5 h-5 rounded-full border-2 border-card flex items-center justify-center"
                              style={{ background: category.color }}
                            >
                              <CategoryIcon className="w-2.5 h-2.5 text-white" />
                            </div>
                            
                            <div className="bg-background rounded-xl overflow-hidden border border-charcoal/5">
                              {/* Photo banner - tap to edit position */}
                              {memory.photo_ids?.length > 0 && (
                                <div
                                  className="relative h-40 bg-muted overflow-hidden cursor-pointer group"
                                  onClick={async () => {
                                    const pid = memory.cover_photo_id || memory.photo_ids[0];
                                    try {
                                      const url = await getPhotoBlobUrl(pid, "feed");
                                      setEditingImage({ memoryId: memory.id, imageUrl: url });
                                    } catch (e) {/* unavailable */}
                                  }}
                                >
                                  <AuthedImage
                                    photoId={memory.cover_photo_id || memory.photo_ids[0]}
                                    alt={memory.title}
                                    className="w-full h-full"
                                    imgStyle={getImageTransform(position)}
                                  />
                                  
                                  {/* Adjust affordance — small bottom-right corner icon.
                                      Always softly visible so mobile users see it; never blocks the photo. */}
                                  <div
                                    className="absolute bottom-2 right-2 w-7 h-7 rounded-full bg-black/55 backdrop-blur-sm text-white flex items-center justify-center opacity-60 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none shadow-md"
                                    aria-hidden="true"
                                    data-testid="memory-adjust-hint"
                                  >
                                    <Move className="w-3.5 h-3.5" />
                                  </div>

                                  {/* Fullscreen / slideshow button — mirrors the
                                      one in the scrapbook so users don't have to
                                      open the editor to actually view their photos. */}
                                  {memory.photo_ids?.length > 0 && (
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      className="absolute bottom-2 left-2 bg-black/55 hover:bg-black/75 text-white h-7 w-7 rounded-full backdrop-blur-sm shadow-md"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        openSlideshow(memory);
                                      }}
                                      data-testid={`memory-fullscreen-${memory.id}`}
                                      aria-label="View photos fullscreen"
                                    >
                                      <Maximize2 className="w-3.5 h-3.5" />
                                    </Button>
                                  )}
                                  
                                  {/* Cover photo indicator */}
                                  {memory.cover_photo_id && (
                                    <div className="absolute top-2 left-2 px-2 py-0.5 bg-amber-500/90 rounded-full text-white text-xs flex items-center gap-1">
                                      <Star className="w-3 h-3 fill-current" />
                                      Cover
                                    </div>
                                  )}
                                  
                                  {/* Set cover photo button */}
                                  {memory.photo_ids && memory.photo_ids.length > 1 && memory.created_by === user?.id && (
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="absolute top-2 right-2 bg-black/40 hover:bg-amber-500 text-white h-7 w-7 p-0 opacity-0 group-hover:opacity-100"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        fetchPhotosForCover(memory);
                                      }}
                                      title="Choose cover photo"
                                    >
                                      <Star className="w-4 h-4" />
                                    </Button>
                                  )}
                                </div>
                              )}
                              
                              <div className="p-4">
                                <div className="flex items-start justify-between mb-2">
                                  <h4 className="font-medium text-charcoal">{memory.title}</h4>
                                  {memory.is_group_item && (
                                    <Users className="w-4 h-4 text-muted-foreground" />
                                  )}
                                </div>
                                
                                {memory.description && (
                                  <p className="text-sm text-muted-foreground mb-3 line-clamp-2">
                                    {memory.description}
                                  </p>
                                )}
                                
                                <div className="flex items-center justify-between text-xs text-muted-foreground">
                                  <div className="flex items-center gap-3">
                                    {memory.photo_count > 0 && (
                                      <span className="flex items-center gap-1">
                                        <ImageIcon className="w-3.5 h-3.5" />
                                        {memory.photo_count}
                                      </span>
                                    )}
                                    {memory.comment_count > 0 && (
                                      <span className="flex items-center gap-1">
                                        <MessageCircle className="w-3.5 h-3.5" />
                                        {memory.comment_count}
                                      </span>
                                    )}
                                  </div>
                                  
                                  <span className="flex items-center gap-1">
                                    <Calendar className="w-3.5 h-3.5" />
                                    {(memory.display_date || memory.completed_at)
                                      ? format(new Date(memory.display_date || memory.completed_at), "MMM d, yyyy")
                                      : "Recently"
                                    }
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>

    {/* Image Position Editor */}
    <ImagePositionEditor
      isOpen={!!editingImage}
      onClose={() => setEditingImage(null)}
      imageUrl={editingImage?.imageUrl}
      initialPosition={editingImage ? (imagePositions[editingImage.memoryId] || { x: 0, y: 0, scale: 1 }) : { x: 0, y: 0, scale: 1 }}
      onSave={(position) => editingImage && handleSavePosition(editingImage.memoryId, position)}
      title="Adjust Photo Position"
    />

    {/* Cover Photo Selection Modal */}
    <Dialog open={!!selectingCover} onOpenChange={() => {
      setSelectingCover(null);
      // Don't revoke — URLs are owned by photoCache and reused next open.
      setAllPhotosForMemory([]);
    }}>
      <DialogContent className="max-w-2xl bg-card">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Star className="w-5 h-5 text-amber-500" />
            Choose Cover Photo
          </DialogTitle>
          <p className="text-sm text-muted-foreground">
            Select a photo to use as the cover for "{selectingCover?.title}"
          </p>
        </DialogHeader>

        <div className="grid grid-cols-3 gap-3 mt-4">
          {allPhotosForMemory.map((photo) => (
            <button
              key={photo.id}
              onClick={() => setCoverPhoto(selectingCover.id, photo.id)}
              className={`relative aspect-square rounded-lg overflow-hidden border-2 transition-all ${
                selectingCover?.cover_photo_id === photo.id 
                  ? "border-amber-500 ring-2 ring-amber-500/30" 
                  : "border-transparent hover:border-primary/50"
              }`}
            >
              <img src={photo.url} alt="" className="w-full h-full object-cover" />
              
              {selectingCover?.cover_photo_id === photo.id && (
                <div className="absolute inset-0 bg-amber-500/20 flex items-center justify-center">
                  <div className="w-8 h-8 rounded-full bg-amber-500 flex items-center justify-center">
                    <Check className="w-5 h-5 text-white" />
                  </div>
                </div>
              )}
            </button>
          ))}
        </div>
      </DialogContent>
    </Dialog>

    {/* Slideshow / fullscreen viewer — sibling Dialog so it opens
        independently of the cover-photo picker. Mirrors Scrapbook so
        users can actually view their photos without going through the
        editor.  Phase-Stabilization fix, 2026-05-08. */}
    <Dialog open={showSlideshow} onOpenChange={setShowSlideshow}>
      <DialogContent
        className="max-w-4xl bg-black border-none p-0"
        data-testid="memory-slideshow"
      >
        <div className="relative h-[80vh] flex items-center justify-center">
          {slideshowPhotos[slideshowIndex] && (
            <img
              src={slideshowPhotos[slideshowIndex].url}
              alt=""
              className="max-w-full max-h-full object-contain"
            />
          )}

          {slideshowPhotos.length > 1 && (
            <>
              <Button
                variant="ghost"
                size="icon"
                onClick={prevSlide}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-white hover:bg-white/10 h-10 w-10 rounded-full"
                data-testid="memory-slideshow-prev"
              >
                <ChevronLeft className="w-6 h-6" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={nextSlide}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-white hover:bg-white/10 h-10 w-10 rounded-full"
                data-testid="memory-slideshow-next"
              >
                <ChevronRight className="w-6 h-6" />
              </Button>
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-white/15 backdrop-blur-sm text-white text-xs">
                {slideshowIndex + 1} / {slideshowPhotos.length}
              </div>
            </>
          )}

          <Button
            variant="ghost"
            size="icon"
            onClick={() => setShowSlideshow(false)}
            className="absolute top-3 right-3 text-white hover:bg-white/10 h-9 w-9 rounded-full"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  </>
  );
};

export default MemoriesTimeline;
